import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatTime } from '../utils/dateFormat';
import { Link } from 'react-router-dom';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getActiveSchoolId } from '../utils/activeSchool';
import { listCashierKkms } from '../services/equipmentApi';
import { AGENT_WS_URL, fetchAgentStatus, startBarcodeListen } from '../services/equipmentApi';
import { getPosPanel, getNomenclatureByBarcode } from '../services/nomenclatureApi';
import { getDiscountCardByNumber } from '../services/loyaltyApi';
import { openPosReceipt, payPosReceipt } from '../services/posApi';
import { PAYMENT_LABELS } from '../utils/paymentLabels';
import FormField, { PosInput } from '../components/pos/FormField';
import PaymentMethodTiles from '../components/pos/PaymentMethodTiles';
import { useSchoolCashDeskEnabled } from '../hooks/useSchoolCashDeskEnabled';
import { getCurrentShift, openShift, closeShift, depositCash, withdrawalCash, getClosePreview, downloadZReportPdf } from '../services/cashShiftApi';
import ZReportView from '../components/pos/ZReportView';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';

const fmt = (n) => (Number(n) || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function calcVat(amount, vatRate) {
    if (vatRate === 'vat20' || vatRate === 'vat120') return amount * 20 / 120;
    if (vatRate === 'vat10' || vatRate === 'vat110') return amount * 10 / 110;
    return 0;
}

function buildLineFromProduct(p, characteristic, unitPcs = 'pcs') {
    const price = characteristic?.price ?? p.price;
    const name = characteristic ? `${p.name} (${characteristic.name})` : p.name;
    return {
        key: `${p.id}-${characteristic?.id || 0}-${Date.now()}`,
        nomenclatureId: p.id,
        characteristicId: characteristic?.id,
        name,
        unitName: p.unitName || unitPcs,
        pricePerUnit: price,
        quantity: 1,
        discountPct: 0,
        vatRate: p.vatRate || 'none',
        paymentSubject: p.paymentSubject || 'commodity',
        paymentMethod54: p.paymentMethod54 || 'full_payment',
        emoji: p.emoji
    };
}

function recalcLines(lines, receiptDiscountPct = 0, bonusRedeem = 0) {
    const items = lines.map(l => {
        const gross = l.pricePerUnit * l.quantity;
        const disc = gross * (l.discountPct / 100);
        const total = gross - disc;
        return { ...l, lineTotal: total, vatAmount: calcVat(total, l.vatRate) };
    });
    const subTotal = items.reduce((s, l) => s + l.pricePerUnit * l.quantity, 0);
    const discountAmount = subTotal * (receiptDiscountPct / 100);
    const vatTotal = items.reduce((s, l) => s + l.vatAmount, 0);
    const total = Math.max(0, subTotal - discountAmount - bonusRedeem);
    return { items, subTotal, discountAmount, vatTotal, total };
}

export default function CashierPage() {
    const { t } = useTranslation(['cashier', 'common', 'errors', 'messages', 'finance', 'nav']);
    const { enabled: cashDeskEnabled, loading: flagLoading } = useSchoolCashDeskEnabled();
    const schoolId = getActiveSchoolId();
    const [kkms, setKkms] = useState([]);
    const [selectedKkm, setSelectedKkm] = useState(null);
    const [panel, setPanel] = useState([]);
    const [lines, setLines] = useState([]);
    const [search, setSearch] = useState('');
    const [screen, setScreen] = useState('main');
    const [payments, setPayments] = useState([{ type: 'cash', amount: '' }]);
    const [cashReceived, setCashReceived] = useState('');
    const [cardQuery, setCardQuery] = useState('');
    const [discountCard, setDiscountCard] = useState(null);
    const [receiptDiscountPct, setReceiptDiscountPct] = useState(0);
    const [bonusRedeem, setBonusRedeem] = useState(0);
    const [payResult, setPayResult] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [scannerOk, setScannerOk] = useState(false);
    const [flashKey, setFlashKey] = useState(null);
    const [tab, setTab] = useState('items');
    const [currentShift, setCurrentShift] = useState(null);
    const [openingBalance, setOpeningBalance] = useState('');
    const [shiftUi, setShiftUi] = useState(null);
    const [zReport, setZReport] = useState(null);
    const [closedShiftId, setClosedShiftId] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [actualCash, setActualCash] = useState('');
    const [shiftMenu, setShiftMenu] = useState(false);
    const [closePreview, setClosePreview] = useState(null);
    const [cashModal, setCashModal] = useState(null);
    const [cashAmount, setCashAmount] = useState('');
    const [cashReason, setCashReason] = useState('');
    const wsRef = useRef(null);

    const totals = useMemo(
        () => recalcLines(lines, discountCard?.discountPercent || receiptDiscountPct, bonusRedeem),
        [lines, discountCard, receiptDiscountPct, bonusRedeem]
    );

    const paidSum = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const remain = Math.max(0, totals.total - paidSum);

    useEffect(() => {
        if (flagLoading || !cashDeskEnabled || !schoolId) return;
        (async () => {
            setLoading(true);
            try {
                const [kRes, pRes] = await Promise.all([
                    listCashierKkms(),
                    getPosPanel(schoolId)
                ]);
                setKkms(kRes.data || []);
                setPanel(pRes.data || []);
                if (kRes.data?.length === 1) setSelectedKkm(kRes.data[0]);
            } catch {
                setError(t('cashier:loadError'));
            }
            setLoading(false);
        })();
    }, [flagLoading, cashDeskEnabled, schoolId]);

    useEffect(() => {
        if (!selectedKkm || !schoolId) return;
        (async () => {
            try {
                const shift = await getCurrentShift(schoolId, selectedKkm.organizationId, selectedKkm.id);
                setCurrentShift(shift || null);
            } catch {
                setCurrentShift(null);
            }
        })();
    }, [selectedKkm, schoolId]);

    async function handleOpenShift() {
        if (!selectedKkm || !schoolId) return;
        try {
            const res = await openShift({
                schoolId,
                organizationId: selectedKkm.organizationId,
                kkmDeviceId: selectedKkm.id,
                openingBalance: parseFloat(openingBalance) || 0
            });
            setCurrentShift(res.data);
            setError('');
        } catch (e) {
            setError(e.response?.data?.message || t('cashier:openShiftError'));
        }
    }

    async function loadClosePreview() {
        if (!currentShift || !schoolId) return;
        const res = await getClosePreview(currentShift.id, schoolId);
        setClosePreview(res.data);
    }

    async function handleCloseShift() {
        if (!currentShift) return;
        try {
            const shiftId = currentShift.id;
            const res = await closeShift(shiftId, schoolId, parseFloat(actualCash) || undefined);
            setClosedShiftId(shiftId);
            setZReport(res.data?.zReport);
            setShiftUi('z');
            setCurrentShift(null);
            setClosePreview(null);
        } catch (e) {
            setError(e.response?.data?.message || t('cashier:closeShiftError'));
        }
    }

    async function submitCashMovement() {
        if (!currentShift || !cashModal) return;
        const amt = parseFloat(cashAmount);
        if (!amt) return;
        if (cashModal === 'deposit') await depositCash(currentShift.id, schoolId, amt, cashReason);
        else await withdrawalCash(currentShift.id, schoolId, amt, cashReason);
        setCashModal(null);
        setCashAmount('');
        setCashReason('');
        const shift = await getCurrentShift(schoolId, selectedKkm.organizationId, selectedKkm.id);
        setCurrentShift(shift || null);
    }

    const unitPcs = t('cashier:unitPcs');

    const addByBarcode = useCallback(async (barcode) => {
        if (!schoolId) return;
        try {
            const res = await getNomenclatureByBarcode(barcode, schoolId);
            const nom = res.data.nomenclature;
            const ch = res.data.characteristic;
            const line = buildLineFromProduct(
                { ...nom, emoji: nom.emoji },
                ch,
                unitPcs
            );
            setLines(prev => {
                const next = [...prev, line];
                setFlashKey(line.key);
                setTimeout(() => setFlashKey(null), 600);
                return next;
            });
        } catch {
            setError(t('cashier:productNotFoundBarcode', { barcode }));
        }
    }, [schoolId, unitPcs, t]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const st = await fetchAgentStatus();
            if (cancelled) return;
            setScannerOk(!!st.alive);
            if (!st.alive) return;
            try {
                await startBarcodeListen();
                const ws = new WebSocket(AGENT_WS_URL);
                wsRef.current = ws;
                ws.onmessage = (ev) => {
                    try {
                        const msg = JSON.parse(ev.data);
                        if (msg.type === 'barcode' && msg.value) addByBarcode(msg.value);
                    } catch { /* ignore */ }
                };
                ws.onclose = () => setScannerOk(false);
            } catch {
                setScannerOk(false);
            }
        })();
        return () => {
            cancelled = true;
            wsRef.current?.close();
        };
    }, [addByBarcode]);

    useEffect(() => {
        const q = search.trim();
        if (/^\d{8,14}$/.test(q)) {
            addByBarcode(q);
            setSearch('');
        }
    }, [search, addByBarcode]);

    async function handleSearchAdd() {
        const q = search.trim();
        if (!q || !schoolId) return;
        if (/^\d{8,14}$/.test(q)) {
            await addByBarcode(q);
            setSearch('');
            return;
        }
        try {
            const api = axios.create({
                baseURL: getApiBaseUrl(),
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const res = await api.get('/nomenclature', { params: { schoolId } });
            const found = (res.data || []).find(n =>
                n.name?.toLowerCase().includes(q.toLowerCase()) ||
                n.article?.toLowerCase().includes(q.toLowerCase())
            );
            if (found) {
                const full = await api.get(`/nomenclature/${found.id}`, { params: { schoolId } });
                setLines(prev => [...prev, buildLineFromProduct(full.data, null, unitPcs)]);
            } else setError(t('cashier:productNotFound'));
        } catch {
            setError(t('cashier:searchError'));
        }
        setSearch('');
    }

    function addPanelTile(tile) {
        setLines(prev => [...prev, buildLineFromProduct(tile, null, unitPcs)]);
    }

    async function lookupCard() {
        if (!cardQuery.trim() || !schoolId) return;
        try {
            const res = await getDiscountCardByNumber(cardQuery.trim(), schoolId);
            setDiscountCard(res.data);
            setReceiptDiscountPct(res.data.discountPercent || 0);
        } catch {
            setError(t('cashier:cardNotFound'));
        }
    }

    const availablePayTypes = useMemo(() => {
        const types = ['cash', 'card_terminal', 'sbp_qr'];
        if (discountCard?.bonusBalance > 0) types.push('bonus');
        if (discountCard) types.push('balance');
        return types;
    }, [discountCard]);

    function goPay() {
        if (!lines.length) { setError(t('cashier:emptyReceipt')); return; }
        setPayments([{ type: 'cash', amount: totals.total.toFixed(2) }]);
        setCashReceived('');
        setScreen('pay');
        setTab('pay');
        setError('');
    }

    function closePay() {
        setScreen('main');
        if (tab === 'pay') setTab('receipt');
    }

    function togglePaymentType(type) {
        setPayments(prev => {
            if (prev.some(p => p.type === type)) return prev.filter(p => p.type !== type);
            const paid = prev.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
            const rest = Math.max(0, totals.total - paid);
            return [...prev, { type, amount: rest > 0 ? rest.toFixed(2) : '' }];
        });
    }

    function setPaymentAmount(type, amount) {
        setPayments(prev => prev.map(p => (p.type === type ? { ...p, amount } : p)));
    }

    async function handlePay() {
        if (!selectedKkm || !schoolId) return;
        if (Math.abs(paidSum - totals.total) > 0.01) {
            setError(t('cashier:paymentMismatch'));
            return;
        }
        setError('');
        try {
            const open = await openPosReceipt({
                schoolId,
                organizationId: selectedKkm.organizationId,
                kkmDeviceId: selectedKkm.id,
                discountCardId: discountCard?.id
            });
            const receiptId = open.data.id;
            const api = axios.create({
                baseURL: getApiBaseUrl(),
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            await api.put(`/pos-receipts/${receiptId}/items`, {
                items: lines.map(l => ({
                    nomenclatureId: l.nomenclatureId,
                    characteristicId: l.characteristicId,
                    name: l.name,
                    quantity: l.quantity,
                    unitName: l.unitName,
                    pricePerUnit: l.pricePerUnit,
                    discountPct: l.discountPct,
                    vatRate: l.vatRate,
                    paymentSubject: l.paymentSubject,
                    paymentMethod54: l.paymentMethod54
                })),
                receiptDiscountPercent: discountCard?.discountPercent || receiptDiscountPct,
                bonusRedeemAmount: bonusRedeem
            }, { params: { schoolId } });

            const payRes = await payPosReceipt(receiptId, schoolId, {
                payments: payments.map(p => ({
                    paymentType: p.type,
                    amount: parseFloat(p.amount) || 0
                })),
                cashReceived: cashReceived ? parseFloat(cashReceived) : null
            });
            setPayResult(payRes.data);
            setScreen('success');
        } catch (e) {
            setError(e.response?.data?.message || t('cashier:payError'));
        }
    }

    function newReceipt() {
        setLines([]);
        setPayments([{ type: 'cash', amount: '' }]);
        setPayResult(null);
        setDiscountCard(null);
        setScreen('main');
        setTab('items');
    }

    if (!flagLoading && !cashDeskEnabled) {
        return (
            <OwnerPageLayout title={t('cashier:title')} maxWidth={560} className="cashier-page pos-ui">
                <p style={{ textAlign: 'center' }}>
                    {t('cashier:sectionDisabled')} {t('cashier:enableInPrefix')}{' '}
                    <Link to="/school/settings">{t('cashier:enableInSettings')}</Link>.
                </p>
            </OwnerPageLayout>
        );
    }

    if (selectedKkm && !currentShift && !loading && screen !== 'success') {
        return (
            <OwnerPageLayout title={t('cashier:titleEmoji')} maxWidth={480} className="cashier-page pos-ui">
                <div className="cashier-success">
                    <h1>{t('cashier:titleEmoji')}</h1>
                    <p style={{ color: 'var(--pos-muted)' }}>{t('cashier:openShiftPrompt')}</p>
                    <p>{t('cashier:registerLabel')} <strong>{selectedKkm.name}</strong></p>
                    <FormField label={t('cashier:openingCash')}>
                        <PosInput type="number" value={openingBalance} onChange={e => setOpeningBalance(e.target.value)} />
                    </FormField>
                    {error && <p className="cashier-error">{error}</p>}
                    <button type="button" className="pos-btn pos-btn--primary" style={{ width: '100%' }} onClick={handleOpenShift}>
                        {t('cashier:openShift')}
                    </button>
                    <button type="button" className="pos-btn pos-btn--ghost" style={{ marginTop: 12 }} onClick={() => setSelectedKkm(null)}>
                        {t('cashier:changeRegister')}
                    </button>
                </div>
            </OwnerPageLayout>
        );
    }

    if (!selectedKkm && !loading) {
        return (
            <OwnerPageLayout title={t('cashier:title')} maxWidth={640} className="cashier-page pos-ui">
                    <p style={{ color: 'var(--pos-muted)' }}>{t('cashier:selectRegister')}</p>
                    {kkms.map(k => (
                        <button key={k.id} type="button" className="cashier-kkm-btn"
                            onClick={() => setSelectedKkm(k)}>
                            🖨 {k.name} · {k.orgName}
                        </button>
                    ))}
                    {kkms.length === 0 && (
                        <p><Link to="/school/venues">{t('cashier:setupEquipment')}</Link></p>
                    )}
            </OwnerPageLayout>
        );
    }

    if (screen === 'success' && payResult) {
        const change = payResult.change;
        return (
            <OwnerPageLayout maxWidth={480} className="cashier-page pos-ui">
                <div className="cashier-success">
                    <h1>{t('cashier:paymentAccepted')}</h1>
                    <p>{t('cashier:receiptLine', { num: payResult.receiptNumber, date: formatDateTime(payResult.paidAt) })}</p>
                    <p className="cashier-success-total">{t('cashier:totalLine', { amount: fmt(payResult.total) })}</p>
                    {change > 0 && <p className="cashier-change">{t('cashier:changeLine', { amount: fmt(change) })}</p>}
                    <button type="button" className="pos-btn pos-btn--primary" onClick={newReceipt}>{t('cashier:newReceipt')}</button>
                </div>
            </OwnerPageLayout>
        );
    }

    const cashPay = payments.filter(p => p.type === 'cash').reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const change = cashReceived ? Math.max(0, parseFloat(cashReceived) - cashPay) : 0;
    const quickCash = [totals.total, Math.ceil(totals.total / 50) * 50, Math.ceil(totals.total / 100) * 100, 1000, 2000]
        .filter((v, i, a) => v > 0 && a.indexOf(v) === i).slice(0, 5);

    const payOverlay = screen === 'pay' && (
        <div className="pos-pay-overlay" role="dialog" aria-modal="true" aria-label={t('cashier:paymentDialog')}
            onClick={e => e.target === e.currentTarget && closePay()}>
            <div className="pos-pay-sheet" onClick={e => e.stopPropagation()}>
                <div className="pos-pay-sheet__head">
                    <button type="button" className="pos-btn pos-btn--ghost" onClick={closePay}>← {t('common:back')}</button>
                    <h2>{fmt(totals.total)} ₽</h2>
                    <p style={{ margin: 0, color: 'var(--pos-muted)', fontSize: 14 }}>{t('cashier:toPay')}</p>
                </div>
                <div className="pos-pay-sheet__body">
                    {error && <p className="cashier-error">{error}</p>}
                    <PaymentMethodTiles
                        types={availablePayTypes}
                        activeTypes={payments.map(p => p.type)}
                        onToggle={togglePaymentType}
                    />
                    {payments.length > 0 && (
                        <div className="pos-pay-amounts">
                            {payments.map(p => {
                                const meta = PAYMENT_LABELS[p.type] || { label: p.type, icon: '💳' };
                                return (
                                    <div key={p.type} className="pos-pay-amount-row">
                                        <span className="pos-pay-amount-row__icon">{meta.icon}</span>
                                        <div className="pos-pay-amount-row__info">
                                            <div className="pos-pay-amount-row__title">{meta.label}</div>
                                        </div>
                                        <div className="pos-pay-amount-row__input">
                                            <PosInput
                                                type="number"
                                                inputMode="decimal"
                                                placeholder="0"
                                                value={p.amount}
                                                onChange={e => setPaymentAmount(p.type, e.target.value)}
                                            />
                                        </div>
                                        <button type="button" className="pos-pay-amount-row__remove"
                                            title={t('cashier:remove')} onClick={() => togglePaymentType(p.type)}>×</button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className={`pos-pay-summary${remain <= 0.01 ? ' pos-pay-summary--ok' : ' pos-pay-summary--warn'}`}>
                        <span>{t('cashier:paidSum', { amount: fmt(paidSum) })}</span>
                        <span>{t('cashier:remainSum', { amount: fmt(remain) })}</span>
                    </div>
                    {payments.some(p => p.type === 'cash') && (
                        <>
                            <FormField label={t('cashier:cashReceived')}>
                                <PosInput
                                    type="number"
                                    inputMode="decimal"
                                    placeholder={fmt(cashPay)}
                                    value={cashReceived}
                                    onChange={e => setCashReceived(e.target.value)}
                                />
                            </FormField>
                            {change > 0 && <p className="pos-pay-change">{t('cashier:changeLine', { amount: fmt(change) })}</p>}
                            <div className="pos-quick-chips">
                                {quickCash.map(v => (
                                    <button key={v} type="button" onClick={() => setCashReceived(String(v))}>
                                        {Number.isInteger(v) ? v : fmt(v)}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="pos-pay-sheet__foot">
                    <button type="button" className="pos-btn pos-btn--primary" style={{ width: '100%', padding: '16px' }}
                        disabled={remain > 0.01 || !payments.length} onClick={handlePay}>
                        {t('cashier:processPayment')}
                    </button>
                </div>
            </div>
        </div>
    );

    const leftPane = (
        <>
            <div className="cashier-search-card">
                <FormField label={t('cashier:barcodeLabel')}>
                    <PosInput
                        placeholder={t('cashier:barcodePlaceholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearchAdd()}
                    />
                </FormField>
                <button type="button" className="pos-btn pos-btn--primary cashier-search-submit" onClick={handleSearchAdd}>
                    {t('common:add')}
                </button>
            </div>
            <div className="cashier-lines">
                {totals.items.map(l => (
                    <div key={l.key} className={`cashier-line ${flashKey === l.key ? 'flash' : ''}`}>
                        <div className="cashier-line-top">
                            <span>{l.emoji || '📦'} {l.name}</span>
                            <button type="button" onClick={() => setLines(lines.filter(x => x.key !== l.key))}>×</button>
                        </div>
                        <div className="cashier-line-controls">
                            <button type="button" onClick={() => setLines(lines.map(x => x.key === l.key
                                ? { ...x, quantity: Math.max(0.001, x.quantity - 1) } : x))}>−</button>
                            <PosInput type="number" min="0.001" step="1" value={l.quantity}
                                onChange={e => setLines(lines.map(x => x.key === l.key
                                    ? { ...x, quantity: parseFloat(e.target.value) || 1 } : x))} />
                            <button type="button" className="cashier-qty-btn" onClick={() => setLines(lines.map(x => x.key === l.key
                                ? { ...x, quantity: x.quantity + 1 } : x))}>+</button>
                            <span className="cashier-line-meta">{fmt(l.pricePerUnit)} ₽ × {l.quantity} = {fmt(l.lineTotal)} ₽</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="cashier-panel">
                <div className="cashier-panel-scroll">
                    {panel.map(tile => (
                        <button key={tile.id} type="button" className="cashier-tile"
                            style={{ background: tile.color || '#e0f2fe' }}
                            onClick={() => addPanelTile(tile)}>
                            <span className="cashier-tile-emoji">{tile.emoji || '📦'}</span>
                            <span className="cashier-tile-name">{tile.name}</span>
                            <span className="cashier-tile-price">{fmt(tile.price)} ₽</span>
                        </button>
                    ))}
                </div>
                <Link to="/nomenclature" className="cashier-panel-config">{t('cashier:configurePanel')}</Link>
            </div>
        </>
    );

    const rightPane = (
        <>
            <div className="cashier-customer">
                <h3>{t('cashier:buyer')}</h3>
                <PosInput placeholder={t('cashier:clientPlaceholder')} value={cardQuery}
                    onChange={e => setCardQuery(e.target.value)} onBlur={lookupCard} />
                {discountCard && (
                    <p>{discountCard.holderName || t('cashier:clientFallback')} · {t('cashier:cardLabel')}: {discountCard.cardNumber}<br />
                        {t('cashier:bonusDiscount', { bonus: discountCard.bonusBalance, pct: discountCard.discountPercent || 0 })}</p>
                )}
            </div>
            <div className="cashier-totals">
                <p>{t('cashier:positionsCount', { count: lines.length })}</p>
                <p>{t('cashier:subtotalLine', { amount: fmt(totals.subTotal) })}</p>
                <p>{t('cashier:discountLine', { amount: fmt(totals.discountAmount) })}</p>
                <p>{t('cashier:vatLine', { amount: fmt(totals.vatTotal) })}</p>
                <hr />
                <p className="cashier-total-big">{t('cashier:totalBig', { amount: fmt(totals.total) })}</p>
            </div>
            <button type="button" className="pos-btn pos-btn--primary cashier-pay-btn" onClick={goPay}
                disabled={!lines.length}>{t('cashier:toPay')}</button>
        </>
    );

    return (
        <OwnerPageLayout maxWidth="100%" className="cashier-page pos-ui">
            {shiftUi === 'close' && currentShift && (
                <div className="pos-pay-overlay">
                    <div className="pos-pay-sheet" style={{ maxWidth: 520 }}>
                        <div className="pos-pay-sheet__head">
                            <h2>{t('cashier:closeShiftTitle', { num: currentShift.shiftNumber })}</h2>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--pos-muted)' }}>
                                {closePreview?.openedAt && t('cashier:period', { from: formatTime(closePreview.openedAt) })}
                            </p>
                        </div>
                        <div className="pos-pay-sheet__body">
                            {!closePreview && <button type="button" className="pos-btn pos-btn--ghost" onClick={loadClosePreview}>{t('cashier:loadClosePreview')}</button>}
                            {closePreview && (
                                <>
                                    <p>{t('cashier:receiptsRefunds', { receipts: closePreview.receiptsCount, refunds: closePreview.refundsCount })}</p>
                                    <p>{t('cashier:cashTotal', { amount: fmt(closePreview.totalCash) })}</p>
                                    <p>{t('cashier:cardTotal', { amount: fmt(closePreview.totalCard) })}</p>
                                    <p>{t('cashier:sbpTotal', { amount: fmt(closePreview.totalSbp) })}</p>
                                    <p>{t('cashier:bonusTotal', { amount: fmt(closePreview.totalBonus) })}</p>
                                    <hr style={{ border: 'none', borderTop: '1px solid var(--pos-border)' }} />
                                    <p><strong>{t('cashier:revenueTotal', { amount: fmt(closePreview.totalRevenue) })}</strong></p>
                                    <p>{t('cashier:vatLine', { amount: fmt(closePreview.vatTotal) })}</p>
                                    <p style={{ marginTop: 12 }}>{t('cashier:cashInRegister')}</p>
                                    <p>{t('cashier:openingBalanceLine', { amount: fmt(closePreview.openingBalance) })}</p>
                                    <p>{t('cashier:cashInOut', { in: fmt(closePreview.cashIn), out: fmt(closePreview.cashOut) })}</p>
                                    <p>{t('cashier:calculatedBalance', { amount: fmt(closePreview.calculatedCashBalance) })}</p>
                                </>
                            )}
                            <FormField label={t('cashier:actualCash')}>
                                <PosInput type="number" value={actualCash} onChange={e => setActualCash(e.target.value)}
                                    placeholder={closePreview ? String(closePreview.calculatedCashBalance) : ''} />
                            </FormField>
                        </div>
                        <div className="pos-pay-sheet__foot" style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="pos-btn pos-btn--ghost" onClick={() => { setShiftUi(null); setClosePreview(null); }}>{t('common:cancel')}</button>
                            <button type="button" className="pos-btn pos-btn--primary" onClick={handleCloseShift}>{t('cashier:closeAndZReport')}</button>
                        </div>
                    </div>
                </div>
            )}
            {shiftUi === 'z' && zReport && (
                <div className="pos-pay-overlay">
                    <div className="pos-pay-sheet" style={{ maxWidth: 480 }}>
                        <div className="pos-pay-sheet__head"><h2>{t('cashier:zReportTitle')}</h2></div>
                        <div className="pos-pay-sheet__body">
                            <ZReportView report={zReport} shiftNumber={zReport.shiftNumber} kkmName={selectedKkm?.name} />
                        </div>
                        <div className="pos-pay-sheet__foot" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {closedShiftId && (
                                <button type="button" className="pos-btn pos-btn--ghost" disabled={pdfLoading}
                                    onClick={async () => {
                                        setPdfLoading(true);
                                        try {
                                            const res = await downloadZReportPdf(closedShiftId, schoolId);
                                            const url = URL.createObjectURL(res.data);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            const cd = res.headers['content-disposition'];
                                            const match = cd && /filename="?([^";]+)"?/.exec(cd);
                                            a.download = match ? match[1] : `z_report_shift${zReport?.shiftNumber || closedShiftId}.pdf`;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        } catch (e) {
                                            setError(e.response?.data?.message || t('cashier:pdfError'));
                                        } finally {
                                            setPdfLoading(false);
                                        }
                                    }}>
                                    {pdfLoading ? '…' : t('cashier:downloadPdf')}
                                </button>
                            )}
                            <button type="button" className="pos-btn pos-btn--primary"
                                onClick={() => { setShiftUi(null); setZReport(null); setClosedShiftId(null); newReceipt(); }}>
                                {t('common:close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {cashModal && (
                <div className="pos-pay-overlay" onClick={() => setCashModal(null)}>
                    <div className="pos-pay-sheet" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="pos-pay-sheet__head">
                            <h2>{cashModal === 'deposit' ? t('cashier:deposit') : t('cashier:withdrawal')}</h2>
                        </div>
                        <div className="pos-pay-sheet__body">
                            <FormField label={t('cashier:amount')}><PosInput type="number" value={cashAmount} onChange={e => setCashAmount(e.target.value)} autoFocus /></FormField>
                            <FormField label={t('cashier:comment')}><PosInput value={cashReason} onChange={e => setCashReason(e.target.value)} /></FormField>
                        </div>
                        <div className="pos-pay-sheet__foot">
                            <button type="button" className="pos-btn pos-btn--primary" style={{ width: '100%' }} onClick={submitCashMovement}>{t('cashier:confirm')}</button>
                        </div>
                    </div>
                </div>
            )}
            <header className="cashier-header">
                <div>
                    <strong>{t('cashier:headerTitle')}</strong>
                    {selectedKkm && <span> · {selectedKkm.name}</span>}
                    {currentShift && (
                        <div style={{ fontSize: 13, color: 'var(--pos-muted)', marginTop: 4 }}>
                            {t('cashier:shiftOpen', { num: currentShift.shiftNumber, time: formatTime(currentShift.openedAt) })}
                            {' · '}{t('cashier:shiftStats', { count: currentShift.totalReceiptsCount, amount: fmt(currentShift.totalRevenue) })}
                        </div>
                    )}
                </div>
                <div>
                    <span className={`cashier-scanner ${scannerOk ? 'ok' : 'bad'}`}>
                        {scannerOk ? t('cashier:scannerOn') : t('cashier:scannerOff')}
                    </span>
                    {currentShift && (
                        <div style={{ position: 'relative', display: 'inline-block', marginLeft: 12 }}>
                            <button type="button" className="pos-btn pos-btn--soft" onClick={() => setShiftMenu(!shiftMenu)}>
                                {t('cashier:closeShiftMenu')}
                            </button>
                            {shiftMenu && (
                                <div className="pos-card" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 50, minWidth: 220, padding: 8 }}>
                                    <button type="button" className="pos-btn pos-btn--ghost" style={{ width: '100%' }}
                                        onClick={() => { setCashModal('deposit'); setShiftMenu(false); }}>{t('cashier:depositBtn')}</button>
                                    <button type="button" className="pos-btn pos-btn--ghost" style={{ width: '100%' }}
                                        onClick={() => { setCashModal('withdrawal'); setShiftMenu(false); }}>{t('cashier:withdrawalBtn')}</button>
                                    <button type="button" className="pos-btn pos-btn--ghost" style={{ width: '100%' }}
                                        onClick={() => { setShiftUi('close'); loadClosePreview(); setShiftMenu(false); }}>{t('cashier:closeShiftBtn')}</button>
                                </div>
                            )}
                        </div>
                    )}
                    <button type="button" className="pos-btn pos-btn--ghost" style={{ marginLeft: 12 }}
                        onClick={() => setSelectedKkm(null)}>{t('cashier:changeRegister')}</button>
                </div>
            </header>
            {error && <p className="cashier-error" style={{ padding: '0 16px' }}>{error}</p>}

            <div className="cashier-tabs cashier-mobile-only">
                {['items', 'receipt', 'pay'].map((tabKey) => (
                    <button key={tabKey} type="button" className={tab === tabKey ? 'active' : ''}
                        onClick={() => { if (tabKey === 'pay') goPay(); else setTab(tabKey); }}>
                        {tabKey === 'items' ? t('cashier:tabItems') : tabKey === 'receipt' ? t('cashier:tabReceipt') : t('cashier:tabPay')}
                    </button>
                ))}
            </div>

            <div className="cashier-layout">
                <div className={`cashier-left cashier-desktop-only`}>{leftPane}</div>
                <div className={`cashier-right cashier-desktop-only`}>{rightPane}</div>

                <div className="cashier-mobile-only cashier-mobile-stack" style={{ width: '100%' }}>
                    <div className={`cashier-tab-panel ${tab === 'items' ? 'active' : ''}`}>{leftPane}</div>
                    <div className={`cashier-tab-panel ${tab === 'receipt' ? 'active' : ''}`}>{rightPane}</div>
                </div>
            </div>
            {payOverlay}
        </OwnerPageLayout>
    );
}